<template>
  <div class="wrap">
    <el-card shadow="never">
      <div
        slot="header"
        class="header"
      >
        <div>
          <h2 class="title">
            Work Order (Grouped by Time Window)
          </h2>
          <div class="sub">
            One row per task time window, with all users shown together
          </div>
        </div>

        <!-- RIGHT SIDE -->
        <div class="header-right">
          <div class="actions">
            <el-select
              v-model="userFilter"
              clearable
              filterable
              size="small"
              placeholder="Filter user"
              style="width: 220px;"
            >
              <el-option
                v-for="u in users"
                :key="u.id"
                :label="u.name"
                :value="u.id"
              />
            </el-select>

            <el-input
              v-model="search"
              size="small"
              clearable
              placeholder="Search: account / proposal / task"
              style="width: 320px;"
            />

            <el-button
              size="small"
              type="primary"
              :loading="loading"
              @click="fetchData"
            >
              Refresh
            </el-button>
          </div>

          <!-- CHECKBOX ROW UNDER ACTIONS -->
          <div class="filters">
            <el-checkbox
              v-model="approvedOnly"
              size="small"
              border
              @change="fetchData"
            >
              Proposals Approved
            </el-checkbox>

            <el-checkbox
              v-model="completedOnly"
              size="small"
              border
              @change="fetchData"
            >
              Proposals Completed
            </el-checkbox>
          </div>
        </div>
      </div>

      <el-alert
        v-if="error"
        :title="error"
        type="error"
        show-icon
        :closable="false"
        class="mb12"
      />

      <el-table
        v-loading="loading"
        :data="groupedRows"
        :row-key="row => `${row.job_proposal_id}|${row.start_ts}|${row.end_ts}`"
        border
        stripe
        size="mini"
        style="width: 100%;"
        :default-sort="{ prop: 'sort_start', order: 'ascending' }"
      >
        <el-table-column
          label="Start"
          width="185"
          prop="sort_start"
          sortable
        >
          <template slot-scope="{ row }">
            <div
              class="mono"
              :class="{ 'overlap-start': row.has_user_overlap }"
            >
              {{ row.start_date_eu }}
            </div>
          </template>
        </el-table-column>

        <el-table-column
          label="End"
          width="185"
          prop="sort_end"
          sortable
        >
          <template slot-scope="{ row }">
            <div class="mono">
              {{ row.end_date_eu }}
            </div>
          </template>
        </el-table-column>

        <el-table-column
          label="Users"
          min-width="220"
        >
          <template slot-scope="{ row }">
            <el-tag
              v-for="u in row.users"
              :key="String(u.id) + '-' + String(u.slot_id)"
              size="mini"
              type="success"
              class="mr6"
            >
              {{ u.name }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column
          label="Task"
          min-width="260"
        >
          <template slot-scope="{ row }">
            <strong>{{ row.task_name }}</strong>
            <span
              v-if="row.layer_name"
              class="muted"
            > ({{ row.layer_name }})</span>
            <div class="muted">
              JP: {{ row.job_proposal_id }}
            </div>
          </template>
        </el-table-column>

        <el-table-column
          label="Proposal"
          width="170"
        >
          <template slot-scope="{ row }">
            <div>Proposal #{{ row.proposal_id }}</div>
            <div class="muted ellipsis">
              {{ row.proposal_pre_description }}
            </div>
          </template>
        </el-table-column>

        <el-table-column
          label="Account"
          width="210"
        >
          <template slot-scope="{ row }">
            <div>{{ row.account_name || ("Account #" + row.account_id) }}</div>
          </template>
        </el-table-column>

        <el-table-column
          label="Min (all users)"
          width="130"
          align="right"
        >
          <template slot-scope="{ row }">
            <div class="mono">
              {{ row.total_minutes_all_users }}
            </div>
          </template>
        </el-table-column>

        <!--  New column: Reassign tasks -->
        <el-table-column
          label="Reassign"
          width="140"
          align="center"
        >
          <template slot-scope="{ row }">
            <el-button
              size="mini"
              type="success"
              :loading="!!row._reassigning"
              @click="reassign_tasks_row(row)"
            >
              Reassign
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script>


export default {
  data() {
    return {
      rows: [],
      loading: false,
      error: null,
      search: '',
      userFilter: null,
      approvedOnly: false,
      completedOnly: false,

      // These are used by your "suggested plan" flow
      // (they won't hurt if you already define them elsewhere)
      suggested_results: null,
    };
  },

  computed: {
    users() {
      const map = new Map();
      (this.rows || []).forEach((r) => {
        const id = r.crm_user_id;
        if (!id) return;
        const name = r.user_name || 'User #' + id;
        if (!map.has(id)) map.set(id, { id, name });
      });
      return Array.from(map.values()).sort((a, b) =>
        a.name.localeCompare(b.name)
      );
    },

    displayRows() {
      const q = (this.search || '').trim().toLowerCase();
      const userId = this.userFilter;

      return (this.rows || []).filter((r) => {
        if (userId && String(r.crm_user_id) !== String(userId)) return false;
        if (!q) return true;

        const blob = [
          r.account_name,
          r.account_id,
          r.proposal_id,
          r.proposal_pre_description,
          r.task_name,
          r.layer_name,
          r.user_name,
          r.job_proposal_id,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return blob.includes(q);
      });
    },

    groupedRows() {
      const map = new Map();
      const rows = this.displayRows || [];

      // 1) Group by (job_proposal_id + exact time window)
      rows.forEach((r) => {
        const startTs =
            Number(r.start_date_raw) ||
            (r.start_date_eu ? Date.parse(r.start_date_eu) / 1000 : 0);
        const endTs =
            Number(r.end_date_raw) ||
            (r.end_date_eu ? Date.parse(r.end_date_eu) / 1000 : 0);

        const key = [r.job_proposal_id, startTs, endTs].join('|');

        if (!map.has(key)) {
          map.set(key, {
            account_id: r.account_id,
            account_name: r.account_name,
            proposal_id: r.proposal_id,
            proposal_pre_description: r.proposal_pre_description,
            job_proposal_id: r.job_proposal_id,
            layer_name: r.layer_name,
            task_name: r.task_name,
            start_date_eu: r.start_date_eu,
            end_date_eu: r.end_date_eu,

            // numeric times for overlap detection + stable sorting
            start_ts: startTs,
            end_ts: endTs,

            // element-ui sorting props
            sort_start: startTs,
            sort_end: endTs,

            users: [],
            total_minutes_all_users: 0,

            // computed later
            has_user_overlap: false,

            // UI state for the new button
            _reassigning: false,
          });
        }

        const g = map.get(key);
        const uid = r.crm_user_id;
        if (uid) {
          g.users.push({
            id: uid,
            name: r.user_name || ('User #' + uid),
            minutes: Number(r.allocated_minutes || 0),
            slot_id: r.slot_id,
          });
        }
        g.total_minutes_all_users += Number(r.allocated_minutes || 0);
      });

      const out = Array.from(map.values());
      out.forEach((g) => g.users.sort((a, b) => a.name.localeCompare(b.name)));

      // 2) Sort groups by time
      out.sort((a, b) => {
        if (a.start_ts < b.start_ts) return -1;
        if (a.start_ts > b.start_ts) return 1;
        if (a.end_ts < b.end_ts) return -1;
        if (a.end_ts > b.end_ts) return 1;
        return 0;
      });

      // 3) Mark overlaps per user:
      // For each user, walk their rows in time order and flag if current.start < prev.end
      const byUser = new Map();
      out.forEach((g) => {
        g.users.forEach((u) => {
          if (!u.id) return;
          if (!byUser.has(u.id)) byUser.set(u.id, []);
          byUser.get(u.id).push(g);
        });
      });

      byUser.forEach((arr) => {
        arr.sort((a, b) => a.start_ts - b.start_ts || a.end_ts - b.end_ts);

        let prev = null;
        arr.forEach((g) => {
          if (prev && g.start_ts < prev.end_ts) {
            g.has_user_overlap = true;
            prev.has_user_overlap = true;
          }
          if (!prev || g.end_ts > prev.end_ts) prev = g;
        });
      });

      return out;
    },
  },

  created() {
    this.fetchData();
  },

  methods: {
    fetchData() {
      this.loading = true;
      this.error = null;

      const params = {
        approved: this.approvedOnly ? 1 : 0,
        completed: this.completedOnly ? 1 : 0,
      };

      this.$http
        .get('/api/proposals/proposal_jobs_tasks_users', { params })
        .then((res) => {
          const payload = res.data;
          this.rows = Array.isArray(payload)
            ? payload
            : Array.isArray(payload?.data)
              ? payload.data
              : Array.isArray(payload?.rows)
                ? payload.rows
                : [];
        }).catch((e) => {
          this.error =
                (e &&
                    e.response &&
                    e.response.data &&
                    e.response.data.error) ||
                e.message ||
                'Request failed';
        })
        .finally(() => {
          this.loading = false;
        });
    },
    // ---------- Existing "reassign" flow you pasted (kept) ----------
    reassign_tasks() {
      this.get_suggested_plan();
    },

    get_suggested_plan() {
      this.loading = true;

      return this.get_suggested_schedule(this.proposal.id).then(
        (response) => {
          this.loading = false;

          if (response.data.status == 0) {
            this.$message({
              type: 'warning',
              dangerouslyUseHTMLString: true,
              message: `Cannot schedule task because of below error(s): <br> ${response.data.errors}`,
            });
          }

          this.suggested_results = response.data.result;
          this.update_view();
          this.$emit('load_job_proposals_list');
          this.check_preferred_date();
        },
        (reason) => {
          console.log(reason);
          this.loading = false;
        }
      );
    },

    // ----------  New: per-row reassign button handler ----------
    async reassign_tasks_row(row) {
      try {
        this.$set(row, '_reassigning', true);

        // Pick the id your scheduling endpoint expects.
        // Most commonly this is proposal_id; if yours expects job_proposal_id, switch it.
        const proposalId = row.proposal_id;
        // const proposalId = row.job_proposal_id;

        await this.get_suggested_plan_for(proposalId);
      } catch (e) {
        console.log(e);
      } finally {
        this.$set(row, '_reassigning', false);
      }
    },

    // Same flow as get_suggested_plan, but accepts the id to schedule for
    get_suggested_plan_for(proposalId) {
      this.loading = true;

      return this.get_suggested_schedule(proposalId).then(
        (response) => {
          this.loading = false;

          if (response.data.status == 0) {
            this.$message({
              type: 'warning',
              dangerouslyUseHTMLString: true,
              message: `Cannot schedule task because of below error(s): <br> ${response.data.errors}`,
            });
          }

          this.suggested_results = response.data.result;
          this.update_view();
          this.$emit('load_job_proposals_list');
          this.check_preferred_date();
        },
        (reason) => {
          console.log(reason);
          this.loading = false;
        }
      );
    },
    get_suggested_schedule(proposal_id) {
      return new Promise((resolve, reject) => {
        let params = {
          proposal_id: proposal_id
        };
        this.$http.post('/get_suggested_schedule/', params).then(
          response => {
            resolve(response);
          },
          reason => {
            console.log(reason);
            reject(reason);
          });
      });
    },
  },
};
</script>

<style scoped>
.wrap {
  padding: 12px;
}
.header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}
.title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
}
.sub {
  color: #909399;
  font-size: 12px;
  margin-top: 2px;
}
.actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.mb12 {
  margin-bottom: 12px;
}
.muted {
  color: #909399;
}
.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
  "Liberation Mono", "Courier New", monospace;
}
.ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 160px;
}
.mr6 {
  margin-right: 6px;
  margin-bottom: 4px;
  display: inline-block;
}

/*  light red background on Start when overlap exists for any user in that row */
.overlap-start {
  background: #ffecec;
  border-radius: 4px;
  padding: 2px 4px;
  display: inline-block;
}

.header-right {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.filters {
  display: flex;
  gap: 1px;
}
</style>
